import { useEffect, useState, useCallback } from "react";
import {
  Button,
  Grid,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import "../../AppPage.css";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../store/auth/hooks";
import { ContentTitleBar } from "../../../components/ContentTitleBar";
import UploadBreadCrumbs from "../../../components/upload/UploadBreadCrumbs";
import UploadStatus from "../../../components/upload/UploadStatus";
import { InfoOutlined } from "@mui/icons-material";
import useWebSocket from "react-use-websocket";
import { WEBSOCKET_URL } from "../../../store/config";
import {
  checkStatusWhitelist,
  getNavigationUrl,
} from "../../../store/navigationUtil";
import { loadClip } from "../utils/dataLoader";
import { useUploadQueue } from "../../../store/uploadQueue/hooks";

export default function AudioUploadNormalizationPage() {
  // This page should be seen only when navigating to a clip that is currently normalizing, it is not part of the regular upload flow
  const theme = useTheme();
  const navigate = useNavigate();
  const { reset: uploadQueueReset, uploadState } = useUploadQueue();
  const { accessCode } = useAuth();
  const [websocketRegistered, setWebsocketRegistered] = useState(false);
  const [totalFiles, setTotalFiles] = useState(0);
  const [completedFiles, setCompletedFiles] = useState(0);
  const percentComplete =
    totalFiles > 0 ? (completedFiles / totalFiles) * 100.0 : 0;

  const { caseId, clipId } = useParams();
  const [clip, setClip] = useState(null);

  const load = useCallback(async () => {
    const currClip = await loadClip(caseId, clipId);
    if (!currClip) {
      // error with login
      navigate(getNavigationUrl(caseId));
      return;
    }
    if (!checkStatusWhitelist(currClip, "NORMALIZING")) {
      navigate(getNavigationUrl(caseId, currClip));
    }
    setClip(currClip);
  }, [caseId, clipId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Setup a websocket
   */
  const { sendMessage } = useWebSocket(WEBSOCKET_URL, {
    share: true,
    onOpen: () => {
      console.log("WebSocket connection established.", WEBSOCKET_URL);
    },
    onMessage: (event) => {
      if (event.data && event.data) {
        const data = JSON.parse(event.data);
        if (
          data.command === "CLIP_STATUS" &&
          data.data.clipId === clip?.id &&
          data.data.clipStatus !== "NORMALIZATION"
        ) {
          navigate(`/app/cases/${caseId}/clips/`);
        } else if (
          data.command === "NORMALIZATION_MESSAGE" &&
          data.data.clipId === clip?.id
        ) {
          //Set State for total and completed files
          setTotalFiles(data.data.totalFiles);
          setCompletedFiles(data.data.completedFiles);
        }
      }
    },
  });

  useEffect(() => {
    if (!websocketRegistered) {
      setWebsocketRegistered(true);
      const payload = {
        command: "REGISTER",
        authorization: accessCode,
      };
      sendMessage(JSON.stringify(payload));
    }
  }, [accessCode, websocketRegistered, sendMessage]);

  return (
    <Stack component="main" height={1}>
      <Stack flex={1} p={2}>
        <UploadBreadCrumbs active={4} />
        <Paper elevation={4} sx={{ p: 3, mt: 2 }}>
          <Grid container spacing={2} justifyContent="space-between">
            <Grid
              size={{ xs: 12, sm: 4, md: 3 }}
              display="flex"
              flexDirection="column"
            >
              <Typography variant="caption">STATUS</Typography>
              <UploadStatus state={uploadState} />
            </Grid>
            <Grid
              size={{ xs: 12, sm: 4, md: 3 }}
              display="flex"
              flexDirection="column"
            >
              <Typography variant="caption">PROCESSED AUDIO FILES</Typography>
              <Typography variant="h5">{totalFiles}</Typography>
            </Grid>
            <Grid
              size={{ xs: 12, sm: 4, md: 3 }}
              display="flex"
              flexDirection="column"
            >
              <Typography variant="caption">PROGRESS</Typography>
              <Typography variant="h5">
                {percentComplete.toFixed(0)}%
              </Typography>
            </Grid>
          </Grid>
        </Paper>
        <Paper
          elevation={4}
          sx={{
            display: "flex",
            alignItems: "center",
            backgroundColor: theme.palette.primary.dark,
            color: theme.palette.common.white,
            mt: 2,
            p: 2,
          }}
        >
          <InfoOutlined />
          <Typography sx={{ ml: 2 }} variant="body1">
            The system is now normalizing the audio files. This may take a few
            minutes.
          </Typography>
        </Paper>
      </Stack>
      <Paper
        elevation={4}
        sx={{
          p: 1,
          mx: 1,
          mb: 1,
        }}
      >
        <Button
          variant="outlined"
          onClick={async () => {
            await uploadQueueReset(clipId);
            navigate(getNavigationUrl(caseId));
          }}
        >
          RETURN TO CLIPS
        </Button>
      </Paper>
      <ContentTitleBar name={clip?.description} label="Clip Source Media" />
    </Stack>
  );
}
